/**
 * AttachMenu — the composer's "+" control.
 *
 * With the Drive connector available it opens a small upward popover (same
 * surface language as the model picker): "Upload from computer" and
 * "Add from Google Drive" — the Claude.ai layout. Without it, the button is
 * simply the classic paperclip that opens the file dialog straight away.
 *
 * Both shapes keep `data-testid="chat-attach-button"`: the journey spotlight
 * anchors to it (`journeyConfig.anchorTestId`), so replacing the plain
 * paperclip with this menu must not move that anchor.
 */

import { memo, useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { GoogleDriveGlyph } from './driveIcons';
import './AttachMenu.css';

interface AttachMenuProps {
  disabled?: boolean;
  driveEnabled: boolean;
  onUploadFromComputer: () => void;
  onAddFromDrive: () => void;
}

function AttachMenuImpl({ disabled, driveEnabled, onUploadFromComputer, onAddFromDrive }: AttachMenuProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  // Which edge of the trigger the popover hangs from. Measured at open time
  // rather than derived from `dir`: the toolbar places the trigger at the
  // physical left in BOTH directions, so a logical inset would push the
  // popover off the composer in Hebrew.
  const [alignEnd, setAlignEnd] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const toggle = () => {
    if (disabled) return;
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) setAlignEnd(rect.left + rect.width / 2 > window.innerWidth / 2);
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!driveEnabled) {
    return (
      <button
        className="attach-btn"
        data-testid="chat-attach-button"
        onClick={onUploadFromComputer}
        disabled={disabled}
        title={t('chat.attachFile')}
        aria-label={t('chat.attachFile')}
        type="button"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
        </svg>
      </button>
    );
  }

  return (
    <div className="attach-menu" ref={rootRef}>
      <button
        className={`attach-btn attach-menu__trigger ${open ? 'is-open' : ''}`}
        data-testid="chat-attach-button"
        onClick={toggle}
        disabled={disabled}
        title={t('chat.attachFile')}
        aria-label={t('chat.attachFile')}
        aria-haspopup="menu"
        aria-expanded={open}
        type="button"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>

      {open && (
        <div className={`attach-menu__popover ${alignEnd ? 'attach-menu__popover--end' : ''}`} role="menu">
          <button
            type="button"
            role="menuitem"
            className="attach-menu__item"
            onClick={() => { setOpen(false); onUploadFromComputer(); }}
          >
            <span className="attach-menu__icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
              </svg>
            </span>
            <span className="attach-menu__label">{t('chat.drive.uploadFromComputer')}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="attach-menu__item"
            onClick={() => { setOpen(false); onAddFromDrive(); }}
          >
            <span className="attach-menu__icon"><GoogleDriveGlyph size={17} /></span>
            <span className="attach-menu__label">{t('chat.drive.addFromDrive')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

export const AttachMenu = memo(AttachMenuImpl);
